import * as angular from "angular";
import {IController, ILogService} from "angular";
import * as _ from "lodash";
import {ViewSettingsHolderType, ViewSettingsType} from "../../models/view-settings.model";
import {ViewSettingsService} from "../../services/view-settings-service.service";
import {CommonsService} from "../../services/commons-service.service";

export class BringozFiltersDropdownPanelComponentCtrl implements IController {
  includeFilters: any[];
  filters: any;
  selectedQuickFilterId?: string;
  pageName?: string;
  queryMap: {};
  excludeIdsMap: any;
  optionIdsMap: any;
  onSubmit: Function;
  onCancel: Function;

  public loading = false;
  public quickFilters: any[] = [];
  public selectedQuickFilter: any;
  public quickFilterToCreate: any;
  public shouldUpdateSelectedQuickFilter = false;
  public searchText = "";
  public selectedFilter: any;
  public filterOptions: any[] = [];
  public activeFiltersList: any[] = [];
  public activeFiltersCount = 0;
  public activeFiltersMap: { [key: string]: boolean } = {};
  public isUnsaved = false;

  private lastFiltersCopy: any;
  private _hoverTimeout: any;
  private _boundMouseMove: (event: MouseEvent) => void;
  private _lastMouseLoc: { x: number; y: number };
  private _prevMouseLoc: { x: number; y: number };

  // Flat delay before switching to a newly-hovered row when no flyout is open yet.
  private readonly HOVER_INTENT_DELAY = 300;
  // How often we re-check the cursor's trajectory while it may still be heading into the open flyout.
  private readonly SAFE_ZONE_POLL_INTERVAL = 100;
  // Vertical forgiveness (px) added to the flyout's near edge for the trajectory check.
  private readonly SAFE_ZONE_TOLERANCE = 60;

  /* @ngInject */
  constructor(
    private viewSettingsService: ViewSettingsService,
    private commonsConstants,
    private $filter,
    private IAMPermissions,
    private $timeout,
    private $state,
    private bringozToastService,
    private $log: ILogService,
    private commonsService: CommonsService,
    private $element
  ) {}

  $onInit() {
    this.pageName = this.pageName || this.$state?.current?.name;
    this._setFiltersOptions();
    if (this.IAMPermissions.hasPermission('viewSettings.list')) {
      this.getQuickFilters();
    }

    this.lastFiltersCopy = angular.copy(this.filters);
    this.updateActiveFilters();

    this._boundMouseMove = (event: MouseEvent) => {
      this._prevMouseLoc = this._lastMouseLoc;
      this._lastMouseLoc = { x: event.clientX, y: event.clientY };
    };
    this.$element[0].addEventListener('mousemove', this._boundMouseMove);
  }

  $onDestroy() {
    this.$timeout.cancel(this._hoverTimeout);
    if (this._boundMouseMove) {
      this.$element[0].removeEventListener('mousemove', this._boundMouseMove);
    }
  }

  $doCheck() {
    if (!angular.equals(this.filters, this.lastFiltersCopy)) {
      this.lastFiltersCopy = angular.copy(this.filters);
      this.updateActiveFilters();
    }
  }

  getQuickFilters() {
    this.loading = true;
    this.viewSettingsService.getByUser(this.pageName, ViewSettingsType.Filter)
      .then((quickFilters: any[]) => {
        this.quickFilters = quickFilters;
        const targetId = this.selectedQuickFilter?.id || this.selectedQuickFilterId;
        if (targetId) {
          this.selectedQuickFilter = _.find(quickFilters, (qf) => qf.id === targetId);
        }
        this.updateActiveFilters();
      })
      .catch((error) => {
        this.bringozToastService.error('Failed to load quick filters');
        this.$log.error('Failed to load quick filters', error);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  canCreateNewFilter() {
    let canCreateNew = false;
    const subFilters = ['noArea', 'noPickUpArea', 'noDropOffArea'];
    _.forOwn(this.filters, (value, key) => {
      if (subFilters.includes(key) && !value) {
        return;
      } else if (_.isArray(value) ? !!value.length : !_.isUndefined(value)) {
        canCreateNew = true;
      }
    });
    return canCreateNew;
  }

  _createQuickFilter() {
    this.quickFilterToCreate.settings = this.filters;
    this.loading = true;
    this.viewSettingsService.createByUser(this.quickFilterToCreate)
      .then((createdQuickFilter) => {
        this.selectedQuickFilter = createdQuickFilter;
        this.quickFilterToCreate = undefined;
        this.getQuickFilters();
        this.updateActiveFilters();
      })
      .catch((error) => {
        this.bringozToastService.error('Failed to save quick filter');
        this.$log.error('Failed to create quick filter', error);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  updateQuickFilter(form?) {
    if (form) {
      form.$setSubmitted(true);
      if (!form.$valid) {
        return;
      }
    }
    this.selectedQuickFilter.settings = this.filters;
    this.loading = true;
    this.viewSettingsService.updateByUser(this.selectedQuickFilter.id, this.selectedQuickFilter)
      .then((updatedQuickFilter) => {
        this.selectedQuickFilter = updatedQuickFilter;
        this.getQuickFilters();
        this.updateActiveFilters();
      })
      .catch((error) => {
        this.bringozToastService.error('Failed to update quick filter');
        this.$log.error('Failed to update quick filter', error);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  deleteQuickFilter() {
    this.loading = true;
    this.viewSettingsService.deleteByUser(this.selectedQuickFilter.id)
      .then(() => {
        this.quickFilters = _.filter(this.quickFilters, (qf) => qf.id !== this.selectedQuickFilter.id);
        this.selectedQuickFilter = undefined;
        this.updateActiveFilters();
      })
      .catch((error) => {
        this.bringozToastService.error('Failed to delete quick filter');
        this.$log.error('Failed to delete quick filter', error);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  onQuickFilterSelect(selectedQuickFilter) {
    this.selectedQuickFilter = selectedQuickFilter;
    if (selectedQuickFilter) {
      this.filters = angular.copy(selectedQuickFilter.settings);
    } else {
      this.onReset();
    }
    this.updateActiveFilters();
  }

  onDeleteSelectedQuickFilter() {
    this.deleteQuickFilter();
  }

  onShouldUpdateSelectedQuickFilterChange(shouldUpdateSelectedQuickFilter) {
    this.shouldUpdateSelectedQuickFilter = !shouldUpdateSelectedQuickFilter;
  }

  toggleCreateQuickFilterMode(isEnabled) {
    this.quickFilterToCreate = isEnabled ? {
      name: undefined,
      pageName: this.pageName,
      holderType: ViewSettingsHolderType.User,
      settingsType: ViewSettingsType.Filter
    } : undefined;
  }

  onChangeFilters(filters) {
    this.filters = filters;
    this.updateActiveFilters();
  }

  onReset() {
    if (this.excludeIdsMap && this.filters.groupIds?.length) {
      this.excludeIdsMap.branchIds = [];
    }
    this.selectedQuickFilter = undefined;
    _.forOwn(this.filters, (value, key) => {
      this.filters[key] = _.isArray(value) ? [] : undefined;
    });
    this.updateActiveFilters();
  }

  onResetAndSubmit() {
    this.onReset();
    this.onSubmit({filters: this.filters, selectedQuickFilter: this.selectedQuickFilter});
  }

  onApply(form) {
    if (form) {
      form.$setSubmitted(true);
      if (form.$valid) {
        const updatedFilters = angular.copy(this.filters);
        _.forOwn(this.filters, (value, key) => {
          if (_.isUndefined(value) || _.isNull(value)) {
            _.unset(updatedFilters, key);
          }
        });

        if (!_.isEqual(updatedFilters, this.selectedQuickFilter?.settings)) {
          this.selectedQuickFilter = undefined;
        }

        this.onSubmit({filters: this.filters, selectedQuickFilter: this.selectedQuickFilter});
      }
    }
  }

  onApplySaveChanges(form) {
    if (form) {
      form.$setSubmitted(true);
      if (form.$valid) {
        if (this.quickFilterToCreate) {
          this._createQuickFilter();
        } else if (this.shouldUpdateSelectedQuickFilter) {
          this.updateQuickFilter(form);
        }
      }
    }
  }

  cancel() {
    this.onCancel();
  }

  onHoverFilter(filter) {
    if (this.quickFilterToCreate || this.selectedFilter?.id === filter.id) {
      return;
    }
    this.$timeout.cancel(this._hoverTimeout);
    this._scheduleHoverActivation(filter);
  }

  onMouseLeavePanel() {
    if (this.quickFilterToCreate) {
      return;
    }
    this.$timeout.cancel(this._hoverTimeout);
    this.selectedFilter = undefined;
  }

  private _scheduleHoverActivation(filter) {
    if (this.selectedFilter && this._isMouseHeadingToFlyout()) {
      // Cursor is still trending toward the currently-open flyout - hold off and re-check shortly
      // instead of swapping, so grazing another row on the way doesn't close it.
      this._hoverTimeout = this.$timeout(() => this._scheduleHoverActivation(filter), this.SAFE_ZONE_POLL_INTERVAL);
      return;
    }

    this._hoverTimeout = this.$timeout(() => {
      this.selectedFilter = filter;
    }, this.HOVER_INTENT_DELAY);
  }

  private _isMouseHeadingToFlyout(): boolean {
    const loc = this._lastMouseLoc;
    if (!loc) {
      return false;
    }
    const flyoutEl = this.$element[0].querySelector('.filters-dropdown-flyout');
    if (!flyoutEl) {
      return false;
    }
    const rect = flyoutEl.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return false;
    }

    // The cursor is already over the open flyout - always safe, regardless of trajectory.
    // Checked before any dx/velocity logic below, since a stopped or settling cursor can report
    // a flat or slightly negative dx even while it's sitting right inside the flyout.
    if (loc.x >= rect.left && loc.x <= rect.right && loc.y >= rect.top && loc.y <= rect.bottom) {
      return true;
    }

    if (!this._prevMouseLoc) {
      return false;
    }
    const apex = this._prevMouseLoc;
    const dx = loc.x - apex.x;

    if (dx <= 0) {
      return false;
    }
    if (loc.x >= rect.left) {
      // Past the flyout's near edge but outside its top/bottom bounds - not actually heading in.
      return false;
    }

    // Extrapolate the current trajectory to the flyout's near edge and see if it lands within
    // its height (plus tolerance) - i.e. whether continuing this path would reach the flyout.
    const extrapolatedY = apex.y + (loc.y - apex.y) * (rect.left - apex.x) / dx;
    return extrapolatedY >= rect.top - this.SAFE_ZONE_TOLERANCE && extrapolatedY <= rect.bottom + this.SAFE_ZONE_TOLERANCE;
  }

  clearHoveredFilter() {
    if (this.quickFilterToCreate) {
      return;
    }
    this.$timeout.cancel(this._hoverTimeout);
    this.selectedFilter = undefined;
  }

  onClickFilterChip(id) {
    const filter = _.find(this.filterOptions, { id });
    if (filter) {
      this.selectedFilter = filter;
    }
  }

  filterByCategoryName = (filter) => {
    if (!this.searchText) {
      return true;
    }
    const label = this.$filter('translate')('SITE_FILTERS.' + filter.id) || '';
    return label.toLowerCase().includes(this.searchText.toLowerCase());
  }

  isActiveFilter(filter) {
    if ((filter.id === 'configuredPackageField' && (!!this.filters.configuredFieldName || !!this.filters.configuredFieldValue))) return true;

    if (filter.type === 'LIST') {
      return (_.get(this.filters[filter.id], 'length')) ||
        (filter.id === 'serviceAreaIds' && !!this.filters.noArea && !!this.filters.noPickUpArea && !!this.filters.noDropOffArea) ||
        (filter.id === 'branchIds' && !!this.filters.noBranches);
    } else {
      return !_.isUndefined(this.filters[filter.id]);
    }
  }

  checkIsUnsaved() {
    if (!this.selectedQuickFilter) {
      return false;
    }
    const cleanFilters = _.pickBy(this.filters, (val) => !_.isUndefined(val));
    const cleanSettings = _.pickBy(this.selectedQuickFilter.settings, (val) => !_.isUndefined(val));
    return !_.isEqual(cleanFilters, cleanSettings);
  }

  updateActiveFilters() {
    this.activeFiltersList = this.getActiveFiltersList();
    this.activeFiltersCount = 0;
    _.forEach(this.activeFiltersList, (item) => {
      this.activeFiltersCount += item.count;
    });

    this.activeFiltersMap = {};
    _.forEach(this.filterOptions, (filter) => {
      this.activeFiltersMap[filter.id] = this.isActiveFilter(filter);
    });

    this.isUnsaved = this.checkIsUnsaved();
  }

  getActiveFiltersList() {
    const list = [];
    const filterNamesMap = {
      isPacked: 'Packed / Not Packed',
      branchIds: 'Operational Unit',
      depotIds: 'Depot',
      serviceAreaIds: 'Service Area',
      agentSkills: 'Service Skill',
      labels: 'Labels',
      resourceIds: 'Resources',
      reasonType: 'Leftover Reasons',
      tags: 'Tags'
    };

    _.forEach(this.filterOptions, (option) => {
      const id = option.id;
      const value = this.filters[id];
      if (id === 'configuredPackageField' && (this.filters.configuredFieldName || this.filters.configuredFieldValue)) {
        list.push({ id: id, label: 'Custom Field', count: 1 });
      } else if (option.type === 'LIST') {
        if (value?.length) {
          list.push({ id: id, label: filterNamesMap[id] || option.id, count: value.length });
        } else if (id === 'serviceAreaIds' && (this.filters.noArea || this.filters.noPickUpArea || this.filters.noDropOffArea)) {
          list.push({ id: id, label: filterNamesMap[id] || option.id, count: 1 });
        } else if (id === 'branchIds' && this.filters.noBranches) {
          list.push({ id: id, label: filterNamesMap[id] || option.id, count: 1 });
        }
      } else {
        if (!_.isUndefined(value) && value !== null && value !== '') {
          list.push({ id: id, label: filterNamesMap[id] || option.id, count: 1 });
        }
      }
    });
    return list;
  }

  onResetFilter(filterName) {
    this.commonsService.resetFilterField(this.filters, filterName, this.excludeIdsMap);
    this.updateActiveFilters();
  }

  private _setFiltersOptions() {
    let filterOptions = this.commonsConstants.siteFilters;
    if (_.get(this.includeFilters, 'length')) {
      filterOptions = _.filter(filterOptions, (sf) => _.includes(this.includeFilters, sf.id));
    }
    _.forEach(filterOptions, (f) => {
      if (f.subFilters) {
        _.forOwn(f.subFilters, (isEnabled, key) => {
          f.subFilters[key] = _.includes(this.includeFilters, key);
        });
      }
    });
    this.filterOptions = filterOptions;
  }
}

angular
  .module('bringoz.admin.commons')
  .component('bringozFiltersDropdownPanel', {
    bindings: {
      includeFilters: '<',
      filters: '<',
      selectedQuickFilterId: '<?',
      pageName: '<?',
      queryMap: '<?',
      excludeIdsMap: '<?',
      optionIdsMap: '<?',
      eventNamePrefix: '<?',
      onSubmit: '&',
      onCancel: '&'
    },
    template: require('./bringoz-filters-dropdown-panel.html').default,
    controller: BringozFiltersDropdownPanelComponentCtrl
  });
